import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { Spinner } from '@consigli/facade';
import { type Blob, type Finding } from '@consigli/types';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Eye, X } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewerContext, ViewerMode } from '@/features/document-viewer/use-viewer-context';
import { STATUS_STYLES } from '@/pages/insight/dashboard/rfp/rfp-timeline-types';
import {
  parseFlexibleDate,
  parseMilestonePayload,
} from '@/pages/insight/dashboard/rfp/rfp-timeline-utils';

import {
  describeMilestoneWhen,
  formatDaysFromToday,
} from './add-context/add-context-milestone-row';
import { AddContextRequirementCard } from './add-context/add-context-requirement-card';
import { FindingCardBody } from './add-context/finding-card-body';
import { findingContextKind } from './add-context/finding-context-kind';
import {
  type CitedFinding,
  countSources,
  presentSourceKinds,
  SOURCE_KINDS,
  type SourceCounts,
  VIEW_KEYS,
} from './sources';
import { type AddContextMenuKind } from './types';
import { useCitedFindings } from './use-cited-findings';
import './add-context/add-context-modal.css';

const ICON_STROKE_WIDTH = 1.75;

const EMPTY_CELL = '–';

type SourcesFilter = 'all' | AddContextMenuKind;

// The design puts Findings before Documents in the filter row, though the list
// itself runs documents first.
const FILTER_ORDER: AddContextMenuKind[] = [
  'finding',
  'milestone',
  'response-requirement',
  'document',
];

const FILTER_LABEL_KEYS: Record<AddContextMenuKind, string> = {
  document: 'prop-chat.sources-documents',
  finding: 'prop-chat.sources-findings',
  milestone: 'prop-chat.sources-milestones',
  'response-requirement': 'prop-chat.sources-response-requirements',
};

const MINI_TEXT =
  'text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)]';

const HEADING_TEXT =
  'text-[length:var(--ds-typography-heading-5-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-5-line-height)] text-[var(--ds-color-text-primary)]';

const FINDING_CARD =
  'flex w-full shrink-0 items-start overflow-hidden rounded-[var(--ds-radius-16)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-6)]';

/** Nothing cited resolved to anything to show. */
const SourcesEmpty: FC<{ message: string }> = ({ message }) => (
  <p className="flex flex-1 items-center justify-center text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
    {message}
  </p>
);

const SourcesScroller: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex min-h-0 flex-1 flex-col gap-[var(--ds-space-3)] overflow-y-auto">
    {children}
  </div>
);

interface SourcesFiltersProps {
  counts: SourceCounts;
  kinds: AddContextMenuKind[];
  onChange: (filter: SourcesFilter) => void;
  value: SourcesFilter;
}

/*
 * Hand-built rather than the design system's FilterPills: those take a string
 * label, and the design gives each pill a count in a lighter colour of its own,
 * on a 32px pill against FilterPills' 36px.
 */
const SourcesFilters: FC<SourcesFiltersProps> = ({ counts, kinds, onChange, value }) => {
  const { t } = useTranslation();
  const filters: SourcesFilter[] = ['all', ...FILTER_ORDER.filter((kind) => kinds.includes(kind))];

  return (
    <div
      role="group"
      aria-label={t('prop-chat.sources-filter')}
      className="flex shrink-0 flex-wrap items-center gap-[var(--ds-space-1-5)]"
    >
      {filters.map((filter) => {
        const isActive = value === filter;
        const count =
          filter === 'all' ? kinds.reduce((sum, kind) => sum + counts[kind], 0) : counts[filter];
        return (
          <button
            key={filter}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(filter)}
            className={clsx(
              'sources-filter flex h-[var(--ds-space-8)] cursor-pointer items-center gap-[var(--ds-space-1-5)] rounded-[var(--ds-radius-8)] border border-solid px-[var(--ds-space-3)] whitespace-nowrap',
              'text-[length:var(--ds-typography-paragraph-13-regular-font-size)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)]',
              isActive
                ? 'border-[var(--ds-color-raw-neutral-900)] bg-[var(--ds-color-raw-neutral-900)] text-[var(--ds-color-text-invert)]'
                : 'border-[var(--ds-color-raw-stone-200)] bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-text-secondary)] hover:bg-[var(--ds-color-bg-secondary-dark)]',
            )}
          >
            {filter === 'all'
              ? t('prop-chat.add-context-filter-all')
              : t(FILTER_LABEL_KEYS[filter])}
            <span
              className={clsx(
                MINI_TEXT,
                'tabular-nums',
                isActive
                  ? 'text-[color-mix(in_srgb,var(--ds-color-text-invert)_60%,transparent)]'
                  : 'text-[var(--ds-color-raw-stone-400)]',
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const SourcesDocumentCard: FC<{ document: Blob; onOpen: (document: Blob) => void }> = ({
  document,
  onOpen,
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative flex w-full shrink-0 items-center overflow-hidden rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)]">
      {/*
       * The name and the eye both open the document in the viewer. The eye is
       * a sibling rather than a child, so a click on it is not also a click on
       * the name; the name is padded on the right by the room it takes.
       */}
      <button
        type="button"
        title={document.name}
        onClick={() => onOpen(document)}
        className="flex min-w-0 flex-1 cursor-pointer items-center py-[var(--ds-space-4)] pl-[var(--ds-space-4)] pr-[calc(var(--ds-space-6)+var(--ds-space-4)+var(--ds-space-4))] text-left hover:bg-[var(--ds-color-bg-secondary-dark)] focus-visible:bg-[var(--ds-color-bg-secondary-dark)] focus-visible:outline-none"
      >
        <span className={clsx('min-w-0 flex-1 truncate', HEADING_TEXT)}>{document.name}</span>
      </button>
      <button
        type="button"
        aria-label={t('prop-chat.add-context-preview', { name: document.name })}
        onClick={() => onOpen(document)}
        className="add-context-preview absolute right-[var(--ds-space-6)] top-1/2 flex size-[var(--ds-space-4)] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[var(--ds-color-bg-icon-primary)]"
      >
        <Eye
          aria-hidden="true"
          strokeWidth={ICON_STROKE_WIDTH}
          className="size-[var(--ds-space-4)]"
        />
      </button>
    </div>
  );
};

// Unread dates ("TBC") go after every dated milestone, as on the RFP dashboard.
const milestoneSortKey = (finding: Finding): number => {
  const payload = parseMilestonePayload(finding);
  const date = payload ? parseFlexibleDate(payload.dateStr) : null;
  return date ? date.getTime() : Number.POSITIVE_INFINITY;
};

// A distance reads well up close and blurs far off: Jun 2023 and Mar 2024 are
// both "3 years ago". Past that, a passed milestone just says so; the tile
// already carries its date.
const RELATIVE_PAST_DAYS = 60;

const SourcesMilestoneRow: FC<{ finding: Finding; today: Date }> = ({ finding, today }) => {
  const { t, i18n } = useTranslation();
  const payload = parseMilestonePayload(finding);
  const date = payload ? parseFlexibleDate(payload.dateStr) : null;
  const when = payload ? describeMilestoneWhen(payload.dateStr, today) : null;
  const status = when?.status ?? null;
  const days = when?.daysFromToday ?? null;
  const detail = date ? when?.time : payload?.dateStr;

  const statusLabel =
    status === null || days === null
      ? null
      : status === 'active'
        ? t('dashboard.rfp-timeline.today')
        : status === 'past' && -days >= RELATIVE_PAST_DAYS
          ? t('prop-chat.sources-milestone-passed')
          : formatDaysFromToday(days, i18n.resolvedLanguage);

  return (
    <li className="flex min-w-0 items-center gap-[var(--ds-space-3-5)] border-t border-solid border-[var(--ds-color-border-primary)] px-[var(--ds-space-3-5)] py-[var(--ds-space-3)] first:border-t-0">
      {/* A calendar leaf, so the date is the first thing the eye lands on. */}
      <div
        aria-hidden="true"
        className={clsx(
          'flex w-[var(--ds-space-12)] shrink-0 flex-col overflow-hidden rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] text-center tabular-nums',
          status === 'past' && 'opacity-70',
        )}
      >
        <span
          className={clsx(
            'py-[var(--ds-space-0-5)] text-[length:var(--ds-typography-paragraph-mini-font-size)] uppercase leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[0.08em]',
            // brand-dark rather than bg-brand, which carries white 12px text at
            // about 3.2:1 and fails AA.
            'text-[var(--ds-color-text-invert)]',
            status === 'active'
              ? 'bg-[var(--ds-color-bg-brand-darkest)]'
              : 'bg-[var(--ds-color-bg-brand-dark)]',
          )}
        >
          {date ? format(date, 'MMM') : EMPTY_CELL}
        </span>
        <span className="text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] text-[var(--ds-color-text-primary)]">
          {date ? format(date, 'd') : EMPTY_CELL}
        </span>
        <span
          className={clsx(
            MINI_TEXT,
            'pb-[var(--ds-space-0-5)] text-[var(--ds-color-text-tertiary)]',
          )}
        >
          {date ? format(date, 'yyyy') : '\u00a0'}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[var(--ds-space-0-5)]">
        <span className="wrap-break-word text-[length:var(--ds-typography-paragraph-small-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-primary)]">
          {payload?.milestone ?? finding.messageTemplate}
        </span>
        {/* The leaf is hidden from a screen reader, so the date is said here. */}
        {date && <span className="sr-only">{when?.date}</span>}
        {/* The time, or the stored text when it is not a date ("TBC"). */}
        {detail && (
          <span className={clsx(MINI_TEXT, 'tabular-nums text-[var(--ds-color-text-tertiary)]')}>
            {detail}
          </span>
        )}
      </div>
      {status && statusLabel && (
        <span
          className={clsx(
            MINI_TEXT,
            'flex shrink-0 items-center gap-[var(--ds-space-1-5)] whitespace-nowrap rounded-full px-[var(--ds-space-2)] py-[var(--ds-space-0-5)]',
            STATUS_STYLES[status].badge,
          )}
        >
          <span
            aria-hidden="true"
            className={clsx(
              'size-[var(--ds-space-1-5)] shrink-0 rounded-full',
              STATUS_STYLES[status].dot,
            )}
          />
          <span className="first-letter:uppercase">{statusLabel}</span>
        </span>
      )}
    </li>
  );
};

/**
 * The cited milestones as one framed list in date order, a calendar leaf
 * leading each row. One block rather than a card apiece, so in the All view
 * they read as a single group between the documents and the findings.
 */
const SourcesMilestoneList: FC<{ findings: Finding[]; today: Date }> = ({ findings, today }) => {
  const sorted = useMemo(
    () => [...findings].sort((a, b) => milestoneSortKey(a) - milestoneSortKey(b)),
    [findings],
  );

  return (
    <ul className="m-0 flex shrink-0 list-none flex-col overflow-hidden rounded-[var(--ds-radius-12)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-0">
      {sorted.map((finding) => (
        <SourcesMilestoneRow key={finding.id} finding={finding} today={today} />
      ))}
    </ul>
  );
};

const SourcesFindingCard: FC<{ finding: Finding }> = ({ finding }) => {
  switch (findingContextKind(finding)) {
    case 'response-requirement':
      return <AddContextRequirementCard finding={finding} />;
    default:
      return (
        <div className={FINDING_CARD}>
          <FindingCardBody finding={finding} />
        </div>
      );
  }
};

interface SourcesModalProps {
  /** Already resolved by the message, which holds the package's blobs anyway. */
  documents: Blob[];
  /** As cited; the findings behind them are fetched here. */
  findings: CitedFinding[];
  onClose: () => void;
  onDocumentClick: (document: Blob) => void;
}

/**
 * What an answer was grounded in, in full: its documents, findings, milestones
 * and response requirements, documents first.
 *
 * The add-context modal's shell, read-only: no checkboxes, no footer, nothing
 * to submit. A document opens in the viewer; the rest have no such target.
 * The filter row appears only when the answer cites more than one kind.
 */
export const SourcesModal: FC<SourcesModalProps> = ({
  documents,
  findings: citedFindings,
  onClose,
  onDocumentClick,
}) => {
  const { t } = useTranslation();
  const { mode } = useViewerContext();
  const [filter, setFilter] = useState<SourcesFilter>('all');
  const today = useMemo(() => new Date(), []);
  const { findings, isLoading } = useCitedFindings(citedFindings);

  // By the finding itself, which is the authority a bare id from the stream lacks.
  const findingsByKind = useMemo(() => {
    const groups: Record<AddContextMenuKind, Finding[]> = {
      document: [],
      finding: [],
      milestone: [],
      'response-requirement': [],
    };
    for (const finding of findings) groups[findingContextKind(finding)].push(finding);
    return groups;
  }, [findings]);

  const counts: SourceCounts = {
    document: documents.length,
    finding: findingsByKind.finding.length,
    milestone: findingsByKind.milestone.length,
    'response-requirement': findingsByKind['response-requirement'].length,
  };
  const kinds = presentSourceKinds(counts);
  const shownKinds = filter === 'all' ? SOURCE_KINDS : [filter];
  /*
   * The title names the one kind when there is only one. Until the findings
   * arrive it goes by the citations, so it opens on what the row that opened
   * it said.
   */
  const titleCounts = isLoading ? countSources(documents.length, citedFindings) : counts;
  const titleKinds = presentSourceKinds(titleCounts);
  const total = titleKinds.reduce((sum, kind) => sum + titleCounts[kind], 0);
  const title =
    titleKinds.length === 1
      ? t(VIEW_KEYS[titleKinds[0]], { count: total })
      : t('prop-chat.source-list', { count: total });

  /*
   * Out of the way while a document from this list is open, rather than closed.
   *
   * The viewer drawer sits at z-75, under this modal's z-80, so the modal would
   * cover it, and the viewer's Close button is an interaction outside this
   * content that `closeOnInteractOutside` would take the list down with.
   * Standing aside instead leaves the caller's state alone, so closing the
   * document comes back to the list it was opened from.
   */
  if (mode === ViewerMode.DocumentFullscreen) return null;

  return (
    <Dialog.Root
      open
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
      closeOnInteractOutside
      closeOnEscape
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_40%,transparent)]" />
        <Dialog.Positioner className="fixed inset-0 z-80 flex items-center justify-center p-[var(--ds-space-4)]">
          <Dialog.Content className="add-context-modal ds-body-font flex h-[min(var(--add-context-modal-height),100%)] w-full max-w-[1024px] flex-col overflow-hidden rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-primary)]">
            <div className="flex shrink-0 items-center justify-between border-b border-solid border-[var(--ds-color-border-primary)] px-[var(--ds-space-8)] py-[var(--ds-space-4)]">
              <Dialog.Title className="min-w-0 flex-1 truncate text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] tracking-[var(--ds-typography-heading-4-letter-spacing)] text-[var(--ds-color-text-primary)]">
                {title}
              </Dialog.Title>
              <Dialog.CloseTrigger
                aria-label={t('prop-chat.add-context-close')}
                className="add-context-close flex size-[var(--ds-space-6)] shrink-0 cursor-pointer items-center justify-center text-[var(--ds-color-bg-icon-primary)]"
              >
                <X
                  aria-hidden="true"
                  strokeWidth={ICON_STROKE_WIDTH}
                  className="size-[var(--ds-space-6)]"
                />
              </Dialog.CloseTrigger>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-[var(--ds-space-3)] px-[var(--ds-space-6)] pt-[var(--ds-space-4)]">
              {isLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Spinner />
                  <span className="sr-only">{t('prop-chat.add-context-loading')}</span>
                </div>
              ) : kinds.length === 0 ? (
                <SourcesEmpty message={t('prop-chat.sources-unavailable')} />
              ) : (
                <>
                  {kinds.length > 1 && (
                    <SourcesFilters
                      counts={counts}
                      kinds={kinds}
                      onChange={setFilter}
                      value={filter}
                    />
                  )}
                  <SourcesScroller>
                    {shownKinds.includes('document') &&
                      documents.map((document) => (
                        <SourcesDocumentCard
                          key={document.id}
                          document={document}
                          onOpen={onDocumentClick}
                        />
                      ))}
                    {shownKinds
                      .filter((kind) => kind !== 'document')
                      .map((kind) =>
                        kind === 'milestone'
                          ? findingsByKind.milestone.length > 0 && (
                              <SourcesMilestoneList
                                key={kind}
                                findings={findingsByKind.milestone}
                                today={today}
                              />
                            )
                          : findingsByKind[kind].map((finding) => (
                              <SourcesFindingCard key={finding.id} finding={finding} />
                            )),
                      )}
                  </SourcesScroller>
                </>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
